import { useState, useEffect, useCallback } from "react";
import { Plus, Edit3, KeyRound, Copy, Check, X, FileText, Trash2 } from "lucide-react";
import { PageHeader } from "../../components/shared/PageHeader";
import { Card } from "../../components/shared/Card";
import { Btn } from "../../components/shared/Btn";
import { Badge } from "../../components/shared/Badge";
import { toast } from "../../components/shared/Toast";
import { useAppData } from "../../lib/AppDataContext";
import { SalaryRevisionModal } from "../../components/shared/SalaryRevisionModal";
import { salaryHistoryApi, salaryFor } from "../../lib/salaryHistoryApi";
import { employeesApi } from "../../lib/employeesApi";
import { api } from "../../lib/api";
import { AddSalesPersonWizard } from "./AddSalesPersonWizard";

/* Sales Team — replicates the OLD admin pattern exactly. A grid of person
   cards (4 cols on lg+); each card has the Edit pencil that opens the
   wizard pre-filled. Add Sales Person opens the same wizard blank.

   The OLD admin has NO delete affordance here — removal happens at the
   Auth/User level, not here. We mirror that. The composition layer in the
   context (seed + overrides + addedSalesPeople) supports edits cleanly. */
export function SalesTeamView() {
  const { allSalesPeople, upsertSalesPerson, deleteSalesPerson } = useAppData();
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [detailPerson, setDetailPerson] = useState(null); // emp being viewed

  /* Salary lives in salary_history, one row per effective month -- the panel
     must show the rate in force now, not Employee.baseSalary, or it will
     disagree with payroll the moment a revision lands. Reloaded after a
     revision so the figure updates without a refresh. */
  const [salaryRows, setSalaryRows] = useState(null);
  const [revising, setRevising] = useState(null);

  const loadSalaryRows = useCallback(() => {
    salaryHistoryApi.listAll().then(setSalaryRows).catch(() => setSalaryRows([]));
  }, []);
  useEffect(() => { loadSalaryRows(); }, [loadSalaryRows]);
  const [resetTarget, setResetTarget] = useState(null);   // emp being reset
  const [resetResult, setResetResult] = useState(null);   // { tempPassword, email }
  const [resetCopied, setResetCopied] = useState(false);

  const openReset = async (person) => {
    if (!window.confirm(`Reset password for ${person.name}? They'll need the new temp password to log in.`)) return;
    try {
      setResetTarget(person);
      const res = await employeesApi.resetPassword(person.id);
      setResetResult(res);
    } catch (e) {
      alert("Reset failed: " + (e?.message || e));
      setResetTarget(null);
    }
  };
  const copyTempPassword = async () => {
    if (resetResult?.tempPassword) {
      try {
        await navigator.clipboard.writeText(resetResult.tempPassword);
        setResetCopied(true);
        setTimeout(() => setResetCopied(false), 1500);
      } catch {}
    }
  };
  const closeReset = () => { setResetTarget(null); setResetResult(null); setResetCopied(false); };

  const openAdd = () => { setEditing(null); setWizardOpen(true); };
  const openEdit = (person) => { setEditing(person); setWizardOpen(true); };
  const onDelete = async (person) => {
    if (!window.confirm(`Delete ${person.name}? This deactivates the employee AND their user account \u2014 they are removed from all active lists, the Assign / Transfer / Executive dropdowns, and can no longer log in. Past records keep their name. Continue?`)) return;
    try {
      await deleteSalesPerson(person.id);
      if (person.userId) {
        await api.patch(`/users/${person.userId}`, { active: false });
      }
      toast(`${person.name} deleted`);
    } catch (e) {
      alert("Delete failed: " + (e?.message || e));
    }
  };

  return (
    <div>
      <PageHeader accent="sales"
        title="Employees"
        subtitle={`${allSalesPeople.filter((s) => s.active !== false).length} employees`}
        actions={
          <Btn variant="primary" onClick={openAdd}>
            <Plus className="w-4 h-4" />Add Employee
          </Btn>
        }
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {allSalesPeople.filter((s) => s.active !== false).map((s) => (
          <Card key={s.id} tone="info" className={"p-4 cursor-pointer hover:ring-2 hover:ring-blue-400 transition " + (s.userActive === false ? "bg-red-50 ring-1 ring-red-200" : "")} onClick={() => setDetailPerson(s)}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center text-sm font-semibold text-slate-600 shrink-0">
                {s.name
                  .split(" ")
                  .map((p) => p[0])
                  .join("")
                  .slice(0, 2)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-slate-900 truncate">{s.name}</div>
                {s.email && (
                  <div className="text-xs text-slate-500 truncate">{s.email}</div>
                )}
              </div>
              {/* Per-card Edit button — opens the same wizard pre-filled. */}
              <button
                onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                title="Edit details"
                className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 p-1.5 rounded-md transition-colors shrink-0"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); openReset(s); }}
                title="Reset password"
                className="text-slate-400 hover:text-amber-600 hover:bg-amber-50 p-1.5 rounded-md transition-colors shrink-0"
              >
                <KeyRound className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(s); }}
                title="Delete (deactivate)"
                className="text-slate-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-md transition-colors shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-slate-100">
              <div>
                <div className="text-xs text-slate-400">Active leads</div>
                <div className="text-lg font-semibold text-slate-900 tabular-nums">
                  {s.active ?? 0}
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-400">Mobile</div>
                <div className="text-sm font-semibold text-slate-900 tabular-nums truncate">
                  {s.phone || "—"}
                </div>
              </div>
            </div>
            {s.fromWizard && (
              <div className="mt-2">
                <Badge cls="bg-blue-50 text-blue-700 ring-blue-600/20 text-[10px]">New hire</Badge>
              </div>
            )}
          </Card>
        ))}
      </div>

      {wizardOpen && (
        <AddSalesPersonWizard
          initial={editing}
          onClose={() => { setWizardOpen(false); setEditing(null); }}
          onSubmit={async (person, isNew) => {
            try {
              await upsertSalesPerson(person, isNew);
              toast(isNew ? `${person.name} added to the team` : `${person.name} updated`);
              setWizardOpen(false);
              setEditing(null);
            } catch (e) {
              toast(`Save failed: ${e?.message || e}`, "error");
            }
          }}
        />
      )}

      {/* Employee detail modal — all filled info + viewable documents */}
      {revising && (
        <SalaryRevisionModal
          employee={revising}
          onClose={() => setRevising(null)}
          onSaved={loadSalaryRows}
        />
      )}

      {detailPerson && (() => {
        const s = detailPerson;
        const MN = ["January","February","March","April","May","June",
                    "July","August","September","October","November","December"];
        const _n = new Date();
        const thisMonthLabel = `${MN[_n.getMonth()]} ${_n.getFullYear()}`;
        const p = s.profile || {};
        const docs = s.documents || {};
        const fmtDate = (d) => d ? String(d).slice(0, 10) : "";
        const fmtMoney = (v) => (v != null && v !== "" && Number(v) > 0) ? "₹" + Number(v).toLocaleString("en-IN") : "";
        const rows = [
          ["Email", s.email], ["Phone", s.phone || p.phone],
          ["Father's Name", p.fathersName], ["D.O.B", fmtDate(p.dob)],
          ["Gender", p.gender], ["Emergency Contact", p.emergencyContact],
          ["Aadhar Number", p.aadharNumber], ["PAN Number", p.panNumber],
          ["Employee Code (ECI)", p.employeeCodeId], ["Department", p.department],
          ["Designation", p.designation], ["Joining Date", fmtDate(p.joiningDate)],
          /* The rate in force NOW, from salary_history -- payroll reads the
             same source. p.salary is Employee.baseSalary, which the form
             still writes but payroll no longer consults; showing it here
             would put this panel and the payroll screen out of step the
             moment a revision lands. */
          ["Salary", fmtMoney(salaryFor(salaryRows, s.id, thisMonthLabel) ?? p.salary)],
          ["Permanent Address", p.permanentAddress], ["Current Address", p.currentAddress],
        ].filter(([, v]) => v != null && String(v).trim() !== "");
        const docList = [
          ["Aadhar", docs.aadhar], ["PAN", docs.pan], ["Education", docs.education],
        ].filter(([, d]) => d && (d.dataUrl || d.url));
        return (
          <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={() => setDetailPerson(null)}>
            <div className="w-full max-w-lg mt-10" onClick={(e) => e.stopPropagation()}>
              <Card className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-slate-100 grid place-items-center text-sm font-semibold text-slate-600">
                      {(s.name || "?").split(" ").map((x) => x[0]).join("").slice(0, 2)}
                    </div>
                    <div>
                      <div className="text-base font-semibold text-slate-900">{s.name}</div>
                      {p.department && <div className="text-xs text-slate-500">{p.designation ? p.designation + " · " : ""}{p.department}</div>}
                    </div>
                  </div>
                  <button onClick={() => setDetailPerson(null)} className="text-slate-400 hover:text-slate-700 p-1 rounded" title="Close">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
                  {rows.map(([k, v]) => (
                    <div key={k} className={k.includes("Address") ? "sm:col-span-2" : ""}>
                      <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{k}</div>
                      <div className="text-sm text-slate-800 break-words">{String(v)}</div>
                    </div>
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => setRevising(s)}
                  className="mt-4 w-full sm:w-auto inline-flex items-center justify-center gap-2 text-sm font-medium rounded-lg ring-1 ring-inset ring-slate-300 px-4 py-2 bg-white text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  Revise salary
                </button>

                {docList.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-slate-100">
                    <div className="text-xs font-semibold text-slate-700 mb-2">Documents</div>
                    <div className="flex flex-wrap gap-2">
                      {docList.map(([label, d]) => (
                        <button
                          key={label}
                          onClick={() => { const u = d.dataUrl || d.url; if (u) window.open(u, "_blank", "noopener,noreferrer"); }}
                          className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 rounded-md px-2.5 py-1.5 text-xs text-slate-700 transition"
                          title={"View " + label}
                        >
                          <FileText className="w-3.5 h-3.5 text-slate-400" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {rows.length === 0 && docList.length === 0 && (
                  <p className="text-sm text-slate-500">No additional details filled yet.</p>
                )}

                <div className="flex justify-end gap-2 mt-6">
                  <Btn variant="secondary" onClick={() => { const per = s; setDetailPerson(null); openEdit(per); }}>Edit</Btn>
                  <Btn variant="primary" onClick={() => setDetailPerson(null)}>Close</Btn>
                </div>
              </Card>
            </div>
          </div>
        );
      })()}

      {/* Phase 5 Bundle C — Reset Password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto">
          <div className="w-full max-w-md mt-12">
            <Card className="p-6">
              <h3 className="text-base font-semibold text-slate-900 mb-2">
                Password reset for {resetTarget.name}
              </h3>
              {!resetResult ? (
                <p className="text-sm text-slate-500">Generating new temporary password…</p>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-4">
                    This password is shown ONCE. Copy it now and share securely with the employee.
                  </p>
                  <div className="bg-slate-50 ring-1 ring-inset ring-slate-200 rounded-lg px-3 py-2.5 flex items-center justify-between mb-4">
                    <code className="text-sm font-mono text-slate-900 tabular-nums">{resetResult.tempPassword}</code>
                    <button
                      onClick={copyTempPassword}
                      className="text-slate-400 hover:text-blue-600 p-1 rounded transition-colors"
                      title="Copy to clipboard"
                    >
                      {resetCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <div className="text-xs text-slate-500 mb-4">
                    Email: <span className="font-medium text-slate-900">{resetResult.email}</span>
                  </div>
                </>
              )}
              <div className="flex justify-end">
                <Btn variant="primary" onClick={closeReset}>Close</Btn>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
